import React from 'react';

interface SummaryItemProps {
  title: string;
  value: number;
  textColor?: string;
}

const SummaryItem: React.FC<SummaryItemProps> = ({ title, value, textColor = "text-gray-900" }) => (
  <div className="flex justify-between items-center">
    <span className="text-sm text-gray-600">{title}</span>
    <span className={`text-lg font-semibold ${textColor}`}>
      ฿{value.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>
  </div>
);

interface SummaryCardProps {
  title: string;
  items: { title: string; value: number; textColor?: string }[];
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, items }) => (
  <div className="bg-white shadow rounded-lg p-6">
    <h2 className="text-xl font-semibold mb-4">{title}</h2>
    <div className="space-y-2">
      {items.map((item, index) => (
        <SummaryItem key={index} {...item} />
      ))}
    </div>
  </div>
);

interface DashboardSummaryProps {
  data: {
    totalPayAmount: number;
    successfulTotalPayAmount: number;
    unsuccessfulTotalPayAmount: number;
    totalNetAmount: number;
    successfulTotalNetAmount: number;
    unsuccessfulTotalNetAmount: number;
    totalFee: number;
    successfulTotalFee: number;
    unsuccessfulTotalFee: number;
    totalUnpaidAmount: number;
    totalPaidAmount: number;
    transactionCount: number;
  };
}

const DashboardSummary: React.FC<DashboardSummaryProps> = ({ data }) => {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      <SummaryCard
        title="ยอดรวม"
        items={[
          { title: "ทั้งหมด", value: data.totalPayAmount },
          { title: "สำเร็จ", value: data.successfulTotalPayAmount, textColor: "text-green-600" },
          { title: "รอดำเนินการ", value: data.unsuccessfulTotalPayAmount, textColor: "text-red-600" },
        ]}
      />
      <SummaryCard
        title="ยอดสุทธิ"
        items={[
          { title: "ทั้งหมด", value: data.totalNetAmount },
          { title: "สำเร็จ", value: data.successfulTotalNetAmount, textColor: "text-green-600" },
          { title: "รอดำเนินการ", value: data.unsuccessfulTotalNetAmount, textColor: "text-red-600" },
        ]}
      />
      <SummaryCard
        title="ค่าธรรมเนียม"
        items={[
          { title: "ทั้งหมด", value: data.totalFee },
          { title: "สำเร็จ", value: data.successfulTotalFee, textColor: "text-green-600" },
          { title: "รอดำเนินการ", value: data.unsuccessfulTotalFee, textColor: "text-red-600" },
        ]}
      />
      <SummaryCard
        title="ยอดเบิก (สำเร็จ)"
        items={[
          { title: "ยังไม่เบิก", value: data.totalUnpaidAmount, textColor: "text-yellow-600" },
          { title: "เบิกแล้ว", value: data.totalPaidAmount, textColor: "text-green-600" },
        ]}
      />
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">จำนวนรายการ</h2>
        <div className="text-3xl font-bold">{data.transactionCount}</div>
      </div>
    </div>
  );
};

export default DashboardSummary;
